import React, { useState } from "react";

/**
 * Wrapper component that allows dragging and dropping files. Dropped files are passed to a callback function.
 * @param {object} props
 * @param {(file: File) => void} [props.onDrop] Called when a file is dropped in the drag zone.
 * @param {React.ReactNode} [props.children] Content for the drag zone.
 */
function DragZone({ onDrop, children }) {
    // Drag events get called for the parent and children elements. Keeping track of the number of times the mouse enters the drag zone
    // allows differentiating between the mouse moving over a child element, and leaving the drag zone.
    const [dragCounter, setDragCounter] = useState(0);

    /**
     * Handles the `onDrop` event.
     * @note Beware of bug in console that causes event.dataTransfer to incorrectly show that there's no files.
     * Source: https://stackoverflow.com/questions/11573710/event-datatransfer-files-is-empty-when-ondrop-is-fired
     * @param {React.DragEvent<HTMLDivElement>} event
     */
    function dropHandler(event) {
        event.preventDefault();

        setDragCounter(dragCounter - 1);

        const file = event.dataTransfer.files[0];
        if (file) {
            onDrop(file);
        }
    }

    /**
     * Handles the `onDragEnter` event.
     * @note `onDragEnter` is triggered when the mouse enters the drop zone. It will also be called when the mouse enters a child element.
     * @param {React.DragEvent<HTMLDivElement>} event
     */
    function dropZoneEnterHandler(event) {
        event.preventDefault();

        setDragCounter(dragCounter + 1);
    }

    /**
     * Handles the `onDragLeave` event.
     * @note `onDragLeave` is triggered when the mouse leaves the drop zone. It will also be called when the mouse leaves a child element.
     * @param {React.DragEvent<HTMLDivElement>} event
     */
    function dropZoneLeaveHandler(event) {
        event.preventDefault();

        // Need to use a function to update state to ensure we have the most recent drag counter value (otherwise a race condition will cause the value to be outdated)
        setDragCounter((previousDragCounter) => previousDragCounter - 1);
    }

    /**
     * Overrides the default drag behaviour for the `onDragOver` event (default behaviour is opening/downloading the file).
     * @note Called every "few hundred" milliseconds when the mouse is over the drop zone.
     * Source: https://developer.mozilla.org/en-US/docs/Web/API/Document/dragover_event
     * @param {React.DragEvent<HTMLDivElement>} event
     */
    function dragOverHandler(event) {
        event.preventDefault();
    }

    return (
        <div
            style={{
                padding: "14px 20px",
                border: "2px dashed var(--state-color-border)",
                borderRadius: "8px",
                opacity: dragCounter > 0 ? 0.5 : 1,
            }}
            onDrop={dropHandler}
            onDragEnter={dropZoneEnterHandler}
            onDragLeave={dropZoneLeaveHandler}
            onDragOver={dragOverHandler}
        >
            {children}
        </div>
    );
}

export default DragZone;
